import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Navbar, CategoryIcon, Logo } from "@/components/shared";
import { groupCategories } from "@/lib/categories";
import { useTranslation } from "react-i18next";
import { catName } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Search, ShieldCheck, Star, CreditCard, ArrowRight, BadgeCheck, Users, Smartphone, Bell,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const HERO_IMG =
    "https://images.unsplash.com/photo-1671804717873-0c10d5c2cafc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxpdGFsaWFuJTIwY2l0eSUyMHN0cmVldCUyMG1vcm5pbmd8ZW58MHx8fHwxNzg3NzUwNTk3fDA&ixlib=rb-4.1.0&q=85";

const stagger = {
    hidden: {},
    show: { transition: { staggerChildren: 0.08 } },
};
const item = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

export default function Landing() {
    const [categories, setCategories] = useState([]);
    const [groups, setGroups] = useState([]);
    const [search, setSearch] = useState("");
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const siteUrl = typeof window !== "undefined" ? window.location.origin : "";

    useEffect(() => {
        api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
        api.get("/category-groups").then((r) => setGroups(r.data)).catch(() => {});
    }, []);

    const grouped = groupCategories(categories, groups);

    const goSearch = () => navigate(`/professionisti?q=${encodeURIComponent(search)}`);

    return (
        <div className="min-h-screen bg-cream">
            <Navbar />

            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="mx-auto max-w-3xl px-5 sm:px-8 pt-20 pb-24 text-center flex flex-col items-center">
                    <motion.div variants={stagger} initial="hidden" animate="show" className="flex flex-col items-center">
                        <motion.span variants={item} className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-1.5 text-xs font-medium tracking-wide text-ink/70">
                            <BadgeCheck className="h-4 w-4 text-terracotta" /> {t("Professionisti verificati vicino a te")}
                        </motion.span>
                        <motion.h1 variants={item} className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-ink leading-[1.05]">
                            {i18n.language.startsWith("en") ? (
                                <>Find the <span className="text-terracotta">right</span> professional</>
                            ) : (
                                <>Trova il professionista <span className="text-terracotta">giusto</span></>
                            )}
                        </motion.h1>
                        <motion.p variants={item} className="mt-6 text-lg leading-relaxed text-ink/70 max-w-xl">
                            {t("Pubblica una richiesta e ricevi proposte da professionisti verificati nella tua zona.")}
                        </motion.p>

                        <motion.div variants={item} className="mt-8 flex items-center gap-2 rounded-full bg-white border border-border p-1.5 shadow-sm w-full max-w-md">
                            <Search className="h-5 w-5 text-muted-foreground ml-3" />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && goSearch()}
                                placeholder={t("Di cosa hai bisogno? (es. idraulico)")}
                                className="border-0 shadow-none focus-visible:ring-0 bg-transparent"
                                data-testid="hero-search-input"
                            />
                            <Button onClick={goSearch} className="rounded-full bg-ink hover:bg-inkhover px-6" data-testid="hero-search-btn">
                                {t("Cerca")}
                            </Button>
                        </motion.div>

                        <motion.div variants={item} className="mt-8 flex items-center gap-6 text-sm text-ink/60">
                            <span className="flex items-center gap-2"><Users className="h-4 w-4 text-terracotta" /> {t("+1.200 professionisti")}</span>
                            <span className="flex items-center gap-2"><Star className="h-4 w-4 fill-terracotta text-terracotta" /> {t("4.9 media recensioni")}</span>
                        </motion.div>
                    </motion.div>
                </div>
                <div className="grain" />
            </section>

            {/* TRUST STRIP */}
            <section className="border-y border-border bg-white">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        { icon: BadgeCheck, t: "Identità verificata", d: "Documenti e competenze controllati dal nostro team." },
                        { icon: Star, t: "Recensioni autentiche", d: "Solo da clienti che hanno completato un lavoro." },
                        { icon: CreditCard, t: "Pagamenti sicuri", d: "Trattenuti in garanzia fino al termine del servizio." },
                    ].map((f) => (
                        <div key={f.t} className="flex items-start gap-4">
                            <div className="h-11 w-11 shrink-0 rounded-xl bg-creamalt flex items-center justify-center">
                                <f.icon className="h-5 w-5 text-terracotta" />
                            </div>
                            <div>
                                <p className="font-heading font-bold text-ink">{t(f.t)}</p>
                                <p className="text-sm text-ink/60 mt-1">{t(f.d)}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="come-funziona" className="bg-ink text-white relative overflow-hidden">
                <div className="grain" />
                <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-20">
                    <p className="uppercase text-xs tracking-[0.2em] font-medium text-terracotta">{t("Come funziona")}</p>
                    <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-bold max-w-2xl">{t("Dalla richiesta al lavoro finito, in fiducia")}</h2>
                    <div className="mt-14 grid md:grid-cols-3 gap-10">
                        {[
                            { n: "01", t: "Cerca o pubblica", d: "Sfoglia i professionisti verificati oppure pubblica la tua richiesta e ricevi offerte." },
                            { n: "02", t: "Scegli con fiducia", d: "Confronta profili, recensioni e prezzi. Prenota il professionista giusto per te." },
                            { n: "03", t: "Paga a lavoro fatto", d: "Il pagamento resta protetto e viene rilasciato solo quando sei soddisfatto." },
                        ].map((s) => (
                            <div key={s.n} className="border-t border-white/15 pt-6">
                                <span className="font-heading text-5xl font-black text-terracotta/90">{s.n}</span>
                                <h3 className="mt-4 font-heading text-xl font-bold">{t(s.t)}</h3>
                                <p className="mt-2 text-white/60 leading-relaxed">{t(s.d)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CATEGORIES */}
            <section id="categorie" className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <p className="uppercase text-xs tracking-[0.2em] font-medium text-terracotta">{t("Categorie")}</p>
                        <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-bold text-ink">{t("Ogni competenza, un professionista")}</h2>
                    </div>
                    <Link to="/professionisti" className="hidden sm:flex items-center gap-1 text-sm font-medium text-ink hover:text-terracotta transition-colors">
                        {t("Vedi tutti")} <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
                <div className="space-y-12">
                    {grouped.map((g) => (
                        <div key={g.slug}>
                            <div className="flex items-center gap-3 mb-5">
                                <h3 className="font-heading text-xl font-bold text-ink" data-testid={`group-${g.slug}`}>{catName(g)}</h3>
                                <span className="h-px flex-1 bg-border" />
                                <span className="text-sm text-ink/40">{g.items.length}</span>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                {g.items.map((c, i) => (
                                    <motion.button
                                        key={c.slug}
                                        initial={{ opacity: 0, y: 16 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: i * 0.03, duration: 0.5 }}
                                        onClick={() => navigate(`/professionisti?category=${c.slug}`)}
                                        className="group text-left rounded-2xl border border-border bg-white p-6 hover:border-terracotta hover:-translate-y-1 transition-[transform,border-color] duration-300"
                                        data-testid={`category-${c.slug}`}
                                    >
                                        <CategoryIcon name={c.icon} size={34} className="text-ink group-hover:text-terracotta transition-colors" />
                                        <p className="mt-4 font-heading font-bold text-ink">{catName(c)}</p>
                                    </motion.button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* MOBILE / QR */}
            <section id="da-telefono" className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
                <div className="rounded-3xl border border-border bg-white p-8 sm:p-12 grid md:grid-cols-2 gap-10 items-center">
                    <div>
                        <p className="uppercase text-xs tracking-[0.2em] font-medium text-terracotta">{t("Da telefono")}</p>
                        <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-bold text-ink">{t("Ciapum in tasca, ovunque")}</h2>
                        <p className="mt-4 text-ink/60 text-base leading-relaxed">
                            {t("Inquadra il codice QR con la fotocamera del telefono per aprire Ciapum sullo smartphone. Gestisci richieste, offerte e messaggi ovunque ti trovi.")}
                        </p>
                        <ul className="mt-6 space-y-3 text-sm text-ink/70">
                            <li className="flex items-center gap-3">
                                <span className="h-8 w-8 rounded-lg bg-creamalt flex items-center justify-center shrink-0"><Smartphone className="h-4 w-4 text-terracotta" /></span>
                                {t("Ottimizzato per l'uso da cellulare")}
                            </li>
                            <li className="flex items-center gap-3">
                                <span className="h-8 w-8 rounded-lg bg-creamalt flex items-center justify-center shrink-0"><Bell className="h-4 w-4 text-terracotta" /></span>
                                {t("Notifiche push per nuove richieste e messaggi")}
                            </li>
                        </ul>
                        <p className="mt-6 text-xs text-ink/40">{t("Su iPhone: apri in Safari → Condividi → “Aggiungi a Home” per attivare le notifiche.")}</p>
                    </div>
                    <div className="flex flex-col items-center gap-4">
                        <div className="rounded-2xl border border-border bg-white p-5 shadow-sm" data-testid="home-qr">
                            <QRCodeSVG value={siteUrl} size={196} level="M" />
                        </div>
                        <a href={siteUrl} className="text-sm font-medium text-terracotta hover:underline break-all text-center max-w-[240px]" data-testid="home-qr-link">
                            {siteUrl.replace(/^https?:\/\//, "")}
                        </a>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24">
                <div className="rounded-3xl bg-terracotta text-white p-10 sm:p-16 relative overflow-hidden">
                    <div className="grain" />
                    <div className="relative max-w-xl">
                        <h2 className="font-heading text-3xl sm:text-4xl font-black">{t("Sei un professionista?")}</h2>
                        <p className="mt-4 text-white/90 text-lg">{t("Ricevi richieste dai clienti della tua zona, costruisci la tua reputazione e vieni pagato in sicurezza.")}</p>
                        <Button
                            onClick={() => navigate("/auth?mode=register&role=professional")}
                            className="mt-8 bg-white text-terracotta hover:bg-white/90 rounded-full px-8 h-12 text-base font-semibold"
                            data-testid="cta-become-pro"
                        >
                            {t("Inizia a lavorare con Ciapum")} <ArrowRight className="h-4 w-4 ml-1" />
                        </Button>
                    </div>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="border-t border-border bg-white">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <Logo testid="footer-logo-link" />
                    <p className="text-sm text-ink/50">© {new Date().getFullYear()} Ciapum — {t("Il professionista giusto, di cui fidarti.")}</p>
                </div>
            </footer>
        </div>
    );
}
